import React, { useMemo, useEffect, useRef, useState } from 'react';
import {
  btnPrimary,
  DASHBOARD_NAV_WIDTH_PX,
  demoShellMobileMediaQuery,
  DEMO_SECTION_STACK_GAP_PX,
  chapterDemoMainPadding,
  DEMO_PRODUCT_INPUT_SCOPE_CLASS,
} from '../designSystem';
import { useNavigate, useParams, Navigate, useLocation } from 'react-router-dom';
import { CHAPTERS } from '../data/chapters';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useApp } from '../context/AppContext';
import { LANDING_SHOWCASE_CHAPTERS } from '../data';
import DashboardNav from '../components/DashboardNav';
import StagePill from '../components/StagePill';
import NovaSidebarWithCollapse from '../components/NovaSidebarWithCollapse';
import PageHeader from '../components/PageHeader';
import { MobileNavDrawer } from '../components/MobileDashboardChrome';
import { FertilityDemoSection } from './FertilityDemoContent';
import { getPageHeaderProps, FERTILITY_DEMO_STATUS_PILL_TEXT } from '../data/pageHeaderConfig';
import { getTtcDemoPresenceBanner } from '../data/ttcDemoPresenceBanner';
import Journey from './Journey';
import Community from './Community';
import FertilityCommunityDemo from './FertilityCommunityDemo';
import WomenLikeMe from './WomenLikeMe';
import ProfileDemo from './ProfileDemo';
import DemoProfilePage from './DemoProfilePage';
import LogoWithBeta from '../components/LogoWithBeta';
import { LOGO_HEIGHT_NAV_COMPACT } from '../components/logoMarkSizes';

const SECTION_ALIASES = {
  dashboard: 'home',
};

const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

const BENEFIT_TO_ID = { ivf: 'fertility', ttc: 'ttc', pregnancy: 'pregnancy', postpartum: 'postpartum', motherhood: 'postpartum' };
const CHAPTER_LABELS = LANDING_SHOWCASE_CHAPTERS.reduce((acc, ch) => {
  acc[BENEFIT_TO_ID[ch.benefitKey] ?? ch.benefitKey] = ch.name;
  return acc;
}, {});

const CHAPTER_PALETTE = {
  ttc: { accent: '#7F77DD', bg: '#EEEDFE', bgDark: 'rgba(127,119,221,0.15)' },
  fertility: { accent: '#D4537E', bg: '#FBEAF0', bgDark: 'rgba(212,83,126,0.12)' },
  pregnancy: { accent: '#14B8A6', bg: '#F0FDFA', bgDark: 'rgba(20,184,166,0.14)' },
  postpartum: { accent: '#9B2942', bg: '#F5EAED', bgDark: 'rgba(155,41,66,0.12)' },
  motherhood: { accent: '#9B2942', bg: '#F5EAED', bgDark: 'rgba(155,41,66,0.12)' },
};

const DEMO_STATS = {
  ttc: [
    { label: 'Cycle day', value: 14 },
    { label: 'Days trying', value: '4 mo' },
    { label: 'TWW countdown', value: '—' },
  ],
  fertility: [
    { label: 'IVF cycles', value: 1 },
    { label: 'Stim days', value: '1–8' },
    { label: 'Follicles (last scan)', value: 12 },
  ],
  pregnancy: [
    { label: 'Weeks pregnant', value: 20 },
    { label: 'Days to due date', value: 140 },
    { label: 'Trimester', value: 2 },
  ],
  postpartum: [
    { label: 'Days since birth', value: 42 },
    { label: 'Baby age', value: '6 wk' },
    { label: 'Mood average', value: '—' },
  ],
  motherhood: [
    { label: 'Days since birth', value: 42 },
    { label: 'Baby age', value: '6 wk' },
    { label: 'Mood average', value: '—' },
  ],
};

const DEMO_SUBTITLES = {
  ttc: 'Cycle day 14 · Fertile window',
  fertility: FERTILITY_DEMO_STATUS_PILL_TEXT,
  pregnancy: 'Week 20 · Second trimester',
  postpartum: '6 weeks postpartum',
  motherhood: '6 weeks postpartum',
};

const SECTION_TITLES = {
  home: 'Home',
  journey: 'My Journey',
  myjourney: 'My Journey',
  tracker: 'Cycle Tracker',
  health: 'Health Record',
  financial: 'Financial Tracker',
  doctors: 'My Doctors',
  medications: 'Medications',
  resources: 'Resources',
  guides: 'Guides',
  products: 'Products',
  profile: 'Profile',
  events: 'Events Near Me',
  community: 'Community',
  connect: 'Connect',
  loss: 'Loss & Recovery',
  wellbeing: 'My Wellbeing',
  activities: 'Activities & Camps',
  birthplan: 'Birth Plan',
  leave: 'Preparing for Leave',
  opk: 'OPK Log',
  tww: 'Two-Week Wait',
  cycle: 'My Cycle',
  'cycle-intelligence': 'Cycle Intelligence',
  'cycle-school': 'Cycle School',
  'appointment-prep': 'Appointment Prep',
  'when-to-see-a-doctor': 'What to Expect',
  'partner-support': 'Partner Support',
  lgbtq: 'LGBTQ+',
  'loss-and-recovery': 'Loss & Recovery',
  learn: 'Learn About My Body',
  mom: 'My Chapter',
  doctor: 'My Doctor',
  milestones: 'Milestone Support',
  working: 'Working Mother',
  baby: 'My Baby',
  hrt: 'HRT & Medications',
  doctorprep: 'Doctor Prep',
  ppd: 'PPD Support',
};

function toTitleCase(str) {
  if (!str) return '';
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (s) => s.toUpperCase());
}

export default function ChapterDemo() {
  const { chapter, section, username } = useParams();
  const navigate = useNavigate();
  const { user, setNovaDraft, showNovaPresenceBar, dismissNovaPresenceBar } = useApp();
  const location = useLocation();
  const m = useMediaQuery(demoShellMobileMediaQuery);
  const activeSection = SECTION_ALIASES[section] || section || 'home';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const resolvedChapter =
    chapter === 'motherhood'
      ? 'postpartum'
      : chapter === 'first-chapter' || chapter === 'cycle' || chapter === 'trying-to-conceive'
        ? 'ttc'
        : chapter === 'menopause'
          ? 'postpartum'
          : chapter;
  const page = useMemo(() => CHAPTERS.find((c) => c.id === resolvedChapter) || CHAPTERS[0], [resolvedChapter]);
  const palette = CHAPTER_PALETTE[resolvedChapter] || CHAPTER_PALETTE.fertility;
  const stats = DEMO_STATS[resolvedChapter] || DEMO_STATS.fertility;
  const subtitle = DEMO_SUBTITLES[resolvedChapter] || 'Your journey';

  const stageInfo = {
    ttc: { icon: '💜', name: 'Trying to Conceive' },
    'trying-to-conceive': { icon: '💜', name: 'Trying to Conceive' },
    fertility: { icon: '🌸', name: 'Fertility' },
    pregnancy: { icon: '🤰', name: 'Pregnancy' },
    postpartum: { icon: '🤱', name: 'Motherhood' },
    motherhood: { icon: '🤱', name: 'Motherhood' },
  }[chapter] || { icon: '🌸', name: page.name };

  const isFertilityDemo = chapter === 'fertility';
  const demoProductInputScope =
    chapter === 'fertility' || chapter === 'ttc' || chapter === 'trying-to-conceive';
  const isMyJourneySection = activeSection === 'journey' || activeSection === 'myjourney';
  // Nova panel on desktop for every chapter shell (Community / Journey / Connect use their own layouts).
  const showNovaSidebar = true;
  const sectionTitle = activeSection ? SECTION_TITLES[activeSection] || activeSection : null;
  const pageTitle = sectionTitle || toTitleCase(activeSection);
  const mainSubtitle = sectionTitle ? (isMyJourneySection ? 'Your story, IVF steps, Nova, women to connect, groups, and resources' : (isFertilityDemo ? subtitle : '')) : subtitle;

  const fullPageSections = [
    'home',
    'myjourney',
    'journey',
    'tracker',
    'cycle',
    'cycle-intelligence',
    'cycle-school',
    'appointment-prep',
    'when-to-see-a-doctor',
    'partner-support',
    'lgbtq',
    'health',
    'community',
    'connect',
    'events',
    'products',
    'milestones',
    'working',
    'leave',
    'baby',
    'wellbeing',
    'activities',
    'opk',
    'tww',
    'learn',
    'mom',
    'doctor',
    'doctors',
    'birthplan',
    'hrt',
    'doctorprep',
    'ppd',
    'loss',
    'loss-and-recovery',
    'resources',
    'guides',
  ];
  const fullPageChapters = ['ttc', 'trying-to-conceive', 'pregnancy', 'postpartum', 'motherhood'];
  const isFullPageContent = fullPageChapters.includes(chapter) && fullPageSections.includes(activeSection);
  const hasPageHeaderConfig = !!getPageHeaderProps(chapter || 'fertility', activeSection, navigate);

  // Top Nova banner: all TTC demo routes (contextual copy + per-path session dismiss); other chapters only on home.
  useEffect(() => {
    const path = location.pathname;

    if (resolvedChapter === 'ttc') {
      const dismissKey = `orivonai:ttcDemoBanner:${path}`;
      if (typeof window !== 'undefined' && sessionStorage.getItem(dismissKey) === '1') {
        dismissNovaPresenceBar();
        return;
      }
      const theme = { accent: palette?.accent || '#7F77DD', accentBg: palette?.bg || '#EEEDFE' };
      const cfg = getTtcDemoPresenceBanner(activeSection, chapter || 'trying-to-conceive', navigate);
      showNovaPresenceBar({
        theme,
        contextPill: cfg.contextPill,
        message: cfg.message,
        actionLabel: cfg.actionLabel,
        onAction: cfg.onAction,
        dismissStorageKey: dismissKey,
      });
      return;
    }

    dismissNovaPresenceBar();
    if (activeSection !== 'home') return;

    const key = resolvedChapter;
    const theme = { accent: palette?.accent || '#D4537E', accentBg: palette?.bg || '#FBEAF0' };

    const NOVA_PRESENCE_BY_CHAPTER = {
      fertility: {
        message: "Scan tomorrow morning — I've prepared what to ask",
      },
      pregnancy: {
        message: "Your anatomy scan is today — I've prepared what to expect and what to ask Dr. Martinez.",
      },
      postpartum: {
        message: "Your 6-week check-in is near — I've prepared what to ask and what to track across recovery.",
      },
    };

    const message = NOVA_PRESENCE_BY_CHAPTER[key]?.message || NOVA_PRESENCE_BY_CHAPTER.fertility.message;
    showNovaPresenceBar({
      theme,
      contextPill: 'Daily check-in',
      message,
      actionLabel: 'See checklist →',
      onAction: () => navigate('/nova-chat'),
    });
  }, [
    resolvedChapter,
    activeSection,
    chapter,
    location.pathname,
    palette,
    navigate,
    showNovaPresenceBar,
    dismissNovaPresenceBar,
  ]);

  // Cycle Tracker route — TTC demo
  if ((chapter === 'cycle' || chapter === 'first-chapter') && (section === 'myjourney' || section === 'doctor')) {
    return <Navigate to="/demo/ttc/tracker" replace />;
  }

  // Demo My Journey: always render latest Journey UI in fertility demo
  if (isMyJourneySection && isFertilityDemo) {
    return <Journey />;
  }
  // Fertility demo community: Connect-aligned design system (see FertilityCommunityDemo).
  if (activeSection === 'community' && chapter === 'fertility') {
    return <FertilityCommunityDemo />;
  }
  // Shared "My Community" layout for other demo chapters (`/demo/:chapter/community`).
  if (activeSection === 'community') {
    return <Community />;
  }
  if (activeSection === 'connect' && isFertilityDemo) {
    return <WomenLikeMe />;
  }
  if (activeSection === 'blog' && chapter) {
    return <Navigate to={`/demo/${chapter}/home`} replace />;
  }

  // Demo profile hub: /demo/fertility/profile · /demo/trying-to-conceive/profile
  if (activeSection === 'profile' && (chapter === 'fertility' || chapter === 'trying-to-conceive')) {
    const profileGridCols = m ? '1fr' : `${DASHBOARD_NAV_WIDTH_PX}px minmax(0, 1fr)`;
    return (
      <>
        <div
          className="demo-layout-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: profileGridCols,
            minHeight: m ? '100dvh' : '100vh',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'clip',
            background: 'var(--sand)',
            fontFamily: FONT_BODY,
            boxSizing: 'border-box',
          }}
        >
          {!m && <DashboardNav />}
          <main
            className={demoProductInputScope ? DEMO_PRODUCT_INPUT_SCOPE_CLASS : undefined}
            style={{
              overflowY: 'auto',
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
              padding: chapterDemoMainPadding(m),
            }}
          >
            <div className="demo-shell-main-inner">
              <DemoProfilePage chapter={chapter} />
            </div>
          </main>
        </div>
        <NovaSidebarWithCollapse
          navigate={navigate}
          setNovaDraft={setNovaDraft}
          chapter={chapter}
          section="profile"
          className={demoProductInputScope ? DEMO_PRODUCT_INPUT_SCOPE_CLASS : undefined}
        />
      </>
    );
  }

  // Profile route: /demo/fertility/profile/:username — single column, nav + Nova panel
  if (username && chapter === 'fertility') {
    const profileGridCols = m ? '1fr' : `${DASHBOARD_NAV_WIDTH_PX}px minmax(0, 1fr)`;
    return (
      <>
        <div
          className="demo-layout-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: profileGridCols,
            minHeight: m ? '100dvh' : '100vh',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'clip',
            background: 'var(--sand)',
            fontFamily: FONT_BODY,
            boxSizing: 'border-box',
          }}
        >
          {!m && <DashboardNav />}
          <main
            className={DEMO_PRODUCT_INPUT_SCOPE_CLASS}
            style={{ overflowY: 'auto', padding: chapterDemoMainPadding(m), width: '100%', minWidth: 0, boxSizing: 'border-box' }}
          >
            <div className="demo-shell-main-inner">
              <ProfileDemo navigate={navigate} />
            </div>
          </main>
        </div>
        <NovaSidebarWithCollapse
          navigate={navigate}
          setNovaDraft={setNovaDraft}
          chapter={chapter}
          className={DEMO_PRODUCT_INPUT_SCOPE_CLASS}
        />
      </>
    );
  }

  const gridCols = m ? '1fr' : `${DASHBOARD_NAV_WIDTH_PX}px minmax(0, 1fr)`;

  return (
    <>
    <div
      className="demo-layout-grid"
      style={{
        display: m ? 'flex' : 'grid',
        flexDirection: m ? 'column' : undefined,
        gridTemplateColumns: m ? undefined : gridCols,
        minHeight: m ? '100dvh' : '100vh',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'clip',
        background: 'var(--sand)',
        fontFamily: FONT_BODY,
        boxSizing: 'border-box',
      }}
    >
      {!m && <DashboardNav />}

      {/* Mobile: slim header with logo + back */}
      {m && (
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            flexShrink: 0,
            background: 'var(--white)',
            borderBottom: `2px solid ${palette.accent}40`,
            padding: 'max(12px, env(safe-area-inset-top)) max(18px, env(safe-area-inset-right)) 14px max(18px, env(safe-area-inset-left))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          <div
            onClick={() => navigate('/')}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, minWidth: 0, flex: 1 }}
          >
            <LogoWithBeta height={LOGO_HEIGHT_NAV_COMPACT} style={{ minWidth: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: palette.accent }}>{stageInfo.name}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: 'var(--text)',
                background: 'var(--sand)',
                border: '1px solid var(--border)',
                cursor: 'pointer',
                padding: '10px 14px',
                borderRadius: 10,
                minHeight: 44,
              }}
              aria-label="Open navigation menu"
            >
              Menu
            </button>
            <button
              type="button"
              // When viewing the demo home, take users to the real site homepage.
              onClick={() => navigate(activeSection === 'home' ? '/' : `/chapter/${page.id}`)}
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: palette.accent,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '6px 0',
              }}
            >
              ← Back
            </button>
          </div>
        </div>
      )}

      {m && mobileMenuOpen && <MobileNavDrawer onClose={() => setMobileMenuOpen(false)} />}

      {/* Main demo content */}
      <main
        style={{
          flex: m ? '1 1 auto' : undefined,
          overflowY: m ? 'visible' : 'auto',
          padding: chapterDemoMainPadding(m),
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: DEMO_SECTION_STACK_GAP_PX,
        }}
      >
        <div
          className="demo-shell-main-inner"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: DEMO_SECTION_STACK_GAP_PX,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
        {/* Skip ChapterDemo header when full-page content or when PageHeader config provides the header */}
        {!isFullPageContent && !hasPageHeaderConfig && activeSection !== 'guides' && activeSection !== 'resources' && (isFertilityDemo && activeSection === 'home' ? (
            <PageHeader
              className="page-header-block--section-stack"
              chapter="fertility"
              eyebrow="Fertility · Home"
              title="Your fertility hub"
              descriptor={(() => {
                const active = (user?.activeChapters || []).map((ch) => BENEFIT_TO_ID[ch] ?? ch);
                const unique = [...new Set(active)];
                if (unique.length >= 2) {
                  const labels = unique.slice(0, 3).map((ch) => CHAPTER_LABELS[ch] || ch).join(' · ');
                  return `${labels} — ${unique.length} chapters active`;
                }
                return 'Your IVF cycle, Nova check-ins, and shortcuts to every chapter tool.';
              })()}
              trailing={
                <button type="button" onClick={() => navigate('/signup?source=chapter-demo')} style={{ ...btnPrimary, background: palette.accent }}>
                  Join the waitlist
                </button>
              }
            />
        ) : (
            <PageHeader
              className="page-header-block--section-stack"
              chapter={resolvedChapter === 'ttc' || chapter === 'trying-to-conceive' ? 'ttc' : 'fertility'}
              eyebrow={activeSection === 'guides' ? 'Fertility · Guides' : `${stageInfo.name === 'Trying to Conceive' ? 'TTC' : stageInfo.name} · ${pageTitle}`}
              title={pageTitle}
              descriptor={
                activeSection === 'guides'
                  ? "Clinically grounded guides for the moments most apps don't prepare you for."
                  : mainSubtitle || subtitle
              }
              trailing={
                <>
                  <StagePill chapter={chapter || 'fertility'} accent={palette.accent} background={palette.bg} />
                  <button type="button" onClick={() => navigate('/signup?source=chapter-demo')} style={{ ...btnPrimary, background: palette.accent }}>
                    Join the waitlist
                  </button>
                </>
              }
            />
        ))}

        <FertilityDemoSection
          key={`${chapter}-${activeSection}`}
          section={activeSection}
          palette={palette}
          m={m}
          navigate={navigate}
          page={page}
          stageInfo={stageInfo}
          chapter={chapter || 'fertility'}
          demoStats={stats}
        />
        </div>
      </main>
    </div>
      {showNovaSidebar && (
        <NovaSidebarWithCollapse
          navigate={navigate}
          setNovaDraft={setNovaDraft}
          chapter={chapter || 'fertility'}
          section={activeSection}
          className={demoProductInputScope ? DEMO_PRODUCT_INPUT_SCOPE_CLASS : undefined}
        />
      )}
    </>
  );
}
